import { yupResolver } from "@hookform/resolvers/yup"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { VideoSquare } from "iconsax-reactjs"
import { AlertCircle } from "lucide-react"
import { useEffect } from "react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"

import { Alert, AlertDescription, Button, Input } from "@/components/ui"
import { submitLinkDraftTAPHighGMV } from "@/features/jobs/manage/api/draft.api"
import { type ECampaignType, EValidateDraft } from "@/modules/helpers/enum"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { schemaWorkingHighGMV } from "@/modules/helpers/schemas"
import type { Potential } from "@/modules/types"

interface WorkingTapHighGMVProps {
  potentials?: Partial<Potential>[]
  campaignType: ECampaignType
  submissionId?: string
  demoIndex: number
  list: any[]
  disabled?: boolean
  workingAt: string
  refetch: () => void
  color?: string
}

export const WorkingTapHighGMV: React.FC<WorkingTapHighGMVProps> = ({
  demoIndex,
  potentials,
  submissionId,
  list,
  refetch
}) => {
  const queryClient = useQueryClient()
  const form = useForm({
    defaultValues: {
      submitUrl: "",
      codeAds: ""
    },
    resolver: yupResolver(schemaWorkingHighGMV)
  })

  const { mutate, isPending } = useMutation({
    mutationKey: ["submit_link_" + potentials?.[0]?.id, submissionId, demoIndex],
    mutationFn: (data: { link?: string; codeAds?: string }) =>
      submitLinkDraftTAPHighGMV({
        // Chỉ gửi submitUrl khi có giá trị (khi link thay đổi)
        submitUrl: data.link,
        kolSubmissionId: submissionId,
        potentialId: potentials?.[0]?.id,
        version: demoIndex,
        codeAds: data?.codeAds
      }),
    onSuccess() {
      toast.success("Cập nhật thành công")
      queryClient.invalidateQueries({
        queryKey: [`check_status_${potentials?.[0]?.id}_${submissionId}`]
      })
      refetch()
    },
    onError(error: any) {
      const type = error?.response?.data?.error?.type
      if (type?.includes(EValidateDraft.LACK_OF_PERMISSION)) {
        form.setError("submitUrl", {
          message:
            "Zizi Project không thể kiểm tra thông tin kênh TikTok của bạn. Vui lòng đăng nhập lại và cấp thêm quyền."
        })
        return
      }
      if (type?.includes(EValidateDraft.VIDEO_IS_NOT_OWNED)) {
        form.setError("submitUrl", { message: "Kênh TikTok của bạn không sở hữu video này." })
        return
      }
      if (type?.includes(EValidateDraft.USER_NOT_LINKING_TIKTOK_YET)) {
        form.setError("submitUrl", { message: "Bạn cần kết nối kênh TikTok." })
        return
      }

      if (type?.includes(EValidateDraft.DEMO_LINK_ALREADY_EXISTED)) {
        form.setError("submitUrl", {
          message: "Đường dẫn bài đăng mới không được trùng với đường dẫn bài đăng cũ trước đó"
        })
        return
      }
      if (type?.includes(EValidateDraft.INVALID_CODE_ADS)) {
        form.setError("codeAds", {
          message: "Mã quảng cáo không hợp lệ"
        })
        return
      }
      form.setError("submitUrl", { message: "Xảy ra lỗi. Vui lòng thử lại sau!" })
    }
  })

  const handleSubmit = ({ submitUrl, codeAds }: { submitUrl: string; codeAds?: string }) => {
    const currentSubmitUrl = list[0]?.submitUrl || ""
    const currentCodeAds = list[0]?.codeAds || ""

    // Kiểm tra có thay đổi không
    const linkChanged = submitUrl && submitUrl !== currentSubmitUrl
    const codeAdsChanged = codeAds && codeAds !== currentCodeAds

    // Không có thay đổi gì thì không gọi API
    if (!linkChanged && !codeAdsChanged) return

    // Chỉ gửi giá trị khi có thay đổi
    mutate({
      link: linkChanged ? submitUrl : undefined,
      codeAds: codeAdsChanged ? codeAds : undefined
    })
  }

  useEffect(() => {
    if (list[0]?.submitUrl) {
      form.setValue("submitUrl", list[0]?.submitUrl)
    }
    if (list[0]?.codeAds) {
      form.setValue("codeAds", list[0]?.codeAds)
    }
  }, [list, form])

  return (
    <div className="flex flex-col gap-2 p-2">
      <div
        className={cn("flex flex-col rounded-xl py-3 px-2 mb-3 bg-background transition-transform")}
      >
        <span className="flex items-center gap-1 text-xs font-medium text-black">
          <VideoSquare size={16} color="#000" variant="Outline" /> Video {demoIndex}
        </span>
        <p className="text-xs text-black mt-2">
          Hãy nhập link và mã quảng cáo TikTok vào các ô bên dưới, đảm bảo đã gắn đúng link
          affiliate từ Zizi Project.
        </p>

        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="flex flex-col gap-3 mt-3 w-full"
        >
          {/* Input submitUrl */}
          <div className="flex flex-col gap-1">
            <p className="text-black !font-normal text-xs">
              Đường dẫn bài đăng <span className="text-red-500">*</span>
            </p>
            <Controller
              name="submitUrl"
              control={form.control}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    id="submitUrl"
                    placeholder="Nhập đường dẫn bài đăng TikTok"
                    disabled={isPending}
                    {...field}
                    className={cn("h-10 rounded-lg text-xs", fieldState.error && "border-red-500")}
                  />
                  {fieldState.error && (
                    <Alert className="p-2">
                      <AlertDescription className="text-xs text-red-500 flex gap-2 items-center">
                        <AlertCircle className="w-4 h-4" /> {fieldState.error.message}
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}
            />
          </div>

          {/* Input codeAds */}
          <div className="flex flex-col gap-1">
            <p className="text-black !font-normal text-xs">Mã quảng cáo</p>
            <Controller
              name="codeAds"
              control={form.control}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    id="codeAds"
                    placeholder="Nhập mã quảng cáo"
                    {...field}
                    className={cn(
                      "h-10 rounded-lg text-xs",
                      list[0]?.codeAds && "bg-gray-100",
                      fieldState.error && "border-red-500"
                    )}
                  />
                  {fieldState.error && (
                    <Alert className="p-2">
                      <AlertDescription className="text-xs text-red-500 flex gap-2 items-center">
                        <AlertCircle className="w-4 h-4" /> {fieldState.error.message}
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}
            />
          </div>

          {/* Button */}
          <Button
            type="submit"
            size="sm"
            disabled={isPending}
            className="rounded-full text-xs w-24 self-end"
          >
            {list[0]?.submitUrl ? "Cập nhật" : "Gửi"}
          </Button>
        </form>
      </div>
    </div>
  )
}
